/**
 * @fileoverview Participating companies on the B2B event form.
 * @module pages/b2bEventForm/useB2BEventParticipants
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addParticipatingCompanyToEvent,
  deleteParticipatingCompany,
  updateParticipatingCompany,
} from "../../logic/b2bEvents";
import { getBuyerCompanies } from "../../logic/buyerCompany";
import { logger, sanitizeError } from "../../utils/logger.js";
import { participantFlowUsesFullPageView } from "./b2bEventFormModel";

/**
 * Skip a company that is already on the event, or a click that is still saving.
 * @param {Object} input
 * @returns {Promise<void>}
 */
const addCompanyToEvent = async ({
  company,
  customerUsers = [],
  activo = true,
  form,
  addingCompanyId,
  setAddingCompanyId,
  setParticipantError,
}) => {
  const isAlreadyAdded = form.participatingCompanies?.some(
    (participant) => participant.idEmpresaCompradora === company._id,
  );
  if (isAlreadyAdded || addingCompanyId === company._id) {
    return;
  }
  setAddingCompanyId(company._id);
  await addParticipatingCompanyToEvent(
    {
      idEvento: form.eventData._id,
      idEmpresaCompradora: company._id,
      activo: Boolean(activo),
      ejecutivosCompradores: Array.isArray(customerUsers) ? customerUsers : [],
    },
    form.setParticipatingCompanies,
    setParticipantError,
    form.navigate,
  );
  setAddingCompanyId(null);
};

/**
 * @param {Object} input
 * @returns {Promise<void>}
 */
const removeParticipatingCompany = async ({
  participantToDelete,
  form,
  setIsDeletingParticipant,
  setParticipantToDelete,
}) => {
  if (!participantToDelete) {
    return;
  }
  const participantWithEvent = {
    ...participantToDelete,
    idEvento: participantToDelete.idEvento ?? form.eventData?._id,
  };
  try {
    setIsDeletingParticipant(true);
    const ok = await deleteParticipatingCompany(participantWithEvent, form.navigate);
    if (ok) {
      form.setParticipatingCompanies((prev) =>
        prev.filter(
          (participant) =>
            participant.idEmpresaCompradora !== participantToDelete.idEmpresaCompradora,
        ),
      );
    }
  } catch (err) {
    logger.error("Error deleting participating company:", { err: sanitizeError(err) });
  } finally {
    setIsDeletingParticipant(false);
    setParticipantToDelete(null);
  }
};

/**
 * @param {Object} input
 * @returns {Promise<void>}
 */
const saveParticipantFlow = async ({
  company,
  customerUsers = [],
  isEdit = false,
  activo = true,
  selectedParticipant,
  form,
  setParticipantError,
  addCompany,
  closeParticipantFlow,
}) => {
  if (isEdit && selectedParticipant) {
    const updated = {
      ...selectedParticipant,
      idEvento: selectedParticipant.idEvento ?? form.eventData?._id,
      activo: Boolean(activo),
      ejecutivosCompradores: Array.isArray(customerUsers) ? customerUsers : [],
    };
    await updateParticipatingCompany(
      updated,
      form.setParticipatingCompanies,
      setParticipantError,
      form.navigate,
    );
  } else {
    await addCompany(company, customerUsers, activo);
  }
  closeParticipantFlow();
};

/**
 * Buyer companies and the search box.
 * @param {Object} form
 * @returns {Object}
 */
const useParticipantSearch = (form) => {
  const [buyerCompanies, setBuyerCompanies] = useState([]);
  const [searchParticipating, setSearchParticipating] = useState("");
  const [participantError, setParticipantError] = useState(null);
  const [addingCompanyId, setAddingCompanyId] = useState(null);

  useEffect(() => {
    if (form.eventData?._id) {
      getBuyerCompanies(form.navigate).then(setBuyerCompanies);
    } else {
      setBuyerCompanies([]);
    }
  }, [form.eventData?._id, form.navigate]);

  const filteredParticipatingCompanies = useMemo(() => {
    if (!searchParticipating.trim()) {
      return form.participatingCompanies ?? [];
    }
    const query = searchParticipating.toLowerCase().trim();
    return (form.participatingCompanies ?? []).filter((company) => {
      const commercial = (company.nombreComercial || "").toLowerCase();
      const legal = (company.razonSocial || "").toLowerCase();
      return commercial.includes(query) || legal.includes(query);
    });
  }, [form.participatingCompanies, searchParticipating]);

  const handleSearchParticipating = useCallback((event) => {
    setSearchParticipating(event.target.value);
  }, []);

  const handleAddParticipatingCompany = useCallback(
    (company, customerUsers, activo) =>
      addCompanyToEvent({
        company,
        customerUsers,
        activo,
        form,
        addingCompanyId,
        setAddingCompanyId,
        setParticipantError,
      }),
    [addingCompanyId, form],
  );

  return {
    buyerCompanies,
    searchParticipating,
    participantError,
    setParticipantError,
    filteredParticipatingCompanies,
    handleSearchParticipating,
    handleAddParticipatingCompany,
  };
};

/**
 * Close the card menu on an outside click, and swap modal vs full page on resize.
 * @param {Object} input
 */
const useParticipantWindow = ({
  dropdownRef,
  setShowDropdown,
  showParticipatingCompanyView,
  setShowParticipatingCompanyView,
  addParticipantModalOpen,
  setAddParticipantModalOpen,
}) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef, setShowDropdown]);

  useEffect(() => {
    const handleResize = () => {
      const usesFullPage = participantFlowUsesFullPageView();
      if (showParticipatingCompanyView && !usesFullPage) {
        setShowParticipatingCompanyView(false);
        setAddParticipantModalOpen(true);
      } else if (addParticipantModalOpen && usesFullPage) {
        setAddParticipantModalOpen(false);
        setShowParticipatingCompanyView(true);
      } else {
        // The open surface already matches the viewport.
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [
    addParticipantModalOpen,
    setAddParticipantModalOpen,
    setShowParticipatingCompanyView,
    showParticipatingCompanyView,
  ]);
};

/**
 * Delete confirmation for one participating company.
 * @param {Object} form
 * @param {Function} setShowDropdown
 * @returns {Object}
 */
const useParticipantDelete = (form, setShowDropdown) => {
  const [deleteParticipantModalOpen, setDeleteParticipantModalOpen] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState(null);
  const [isDeletingParticipant, setIsDeletingParticipant] = useState(false);
  const deleteParticipantDialogRef = useRef(null);

  const handleOpenDeleteParticipantModal = useCallback((event, participant) => {
    event?.stopPropagation?.();
    setParticipantToDelete(participant);
    setDeleteParticipantModalOpen(true);
    setShowDropdown(null);
  }, [setShowDropdown]);

  const handleCloseDeleteParticipantModal = useCallback(() => {
    setDeleteParticipantModalOpen(false);
    setParticipantToDelete(null);
    setIsDeletingParticipant(false);
  }, []);

  const handleConfirmDeleteParticipant = useCallback(
    () =>
      removeParticipatingCompany({
        participantToDelete,
        form,
        setIsDeletingParticipant,
        setParticipantToDelete,
      }),
    [form, participantToDelete],
  );

  return {
    deleteParticipantModalOpen,
    isDeletingParticipant,
    deleteParticipantDialogRef,
    handleOpenDeleteParticipantModal,
    handleCloseDeleteParticipantModal,
    handleConfirmDeleteParticipant,
  };
};

/**
 * Modal, full-page editor, and the card menu. Resize keeps the open surface in sync.
 * @param {Object} form
 * @param {Object} search
 * @returns {Object}
 */
const useParticipantEditor = (form, search) => {
  const [addParticipantModalOpen, setAddParticipantModalOpen] = useState(false);
  const [showParticipatingCompanyView, setShowParticipatingCompanyView] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [showDropdown, setShowDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const deletion = useParticipantDelete(form, setShowDropdown);

  const openEditor = useCallback((participant) => {
    setSelectedParticipant(participant);
    setShowDropdown(null);
    if (participantFlowUsesFullPageView()) {
      setShowParticipatingCompanyView(true);
    } else {
      setAddParticipantModalOpen(true);
    }
  }, []);

  const editParticipant = useCallback(
    (event, participant) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      openEditor(participant);
    },
    [openEditor],
  );

  const openAddParticipant = useCallback(() => {
    setSelectedParticipant(null);
    if (participantFlowUsesFullPageView()) {
      setShowParticipatingCompanyView(true);
    } else {
      setAddParticipantModalOpen(true);
    }
  }, []);

  const closeParticipantFlow = useCallback(() => {
    search.setParticipantError(null);
    setAddParticipantModalOpen(false);
    setShowParticipatingCompanyView(false);
    setSelectedParticipant(null);
  }, [search]);

  const toggleDropdown = useCallback((event, index) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setShowDropdown((prev) => (prev === index ? null : index));
  }, []);

  const handleParticipantFlowSuccess = useCallback(
    (company, customerUsers, isEdit, activo) =>
      saveParticipantFlow({
        company,
        customerUsers,
        isEdit,
        activo,
        selectedParticipant,
        form,
        setParticipantError: search.setParticipantError,
        addCompany: search.handleAddParticipatingCompany,
        closeParticipantFlow,
      }),
    [closeParticipantFlow, form, search, selectedParticipant],
  );

  useParticipantWindow({
    dropdownRef,
    setShowDropdown,
    showParticipatingCompanyView,
    setShowParticipatingCompanyView,
    addParticipantModalOpen,
    setAddParticipantModalOpen,
  });

  return {
    addParticipantModalOpen,
    showParticipatingCompanyView,
    selectedParticipant,
    showDropdown,
    dropdownRef,
    editParticipant,
    openAddParticipant,
    closeParticipantFlow,
    toggleDropdown,
    handleParticipantFlowSuccess,
    ...deletion,
  };
};

/**
 * Participating companies: search, add, edit, and delete.
 * @param {Object} form
 * @returns {Object}
 */
export const useB2BEventParticipants = (form) => {
  const search = useParticipantSearch(form);
  const editor = useParticipantEditor(form, search);
  return {
    buyerCompanies: search.buyerCompanies,
    participantError: search.participantError,
    searchParticipating: search.searchParticipating,
    filteredParticipatingCompanies: search.filteredParticipatingCompanies,
    handleSearchParticipating: search.handleSearchParticipating,
    ...editor,
  };
};
